import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SignatureInfo } from 'src/app/core/model/signature';
import { SignatureService } from 'src/app/core/services/signature.service';

@Component({
  selector: 'app-signature',
  templateUrl: './signature.component.html'
})
export class SignatureComponent implements OnInit {
  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;

  info: SignatureInfo | null = null;
  loading = true;
  submitting = false;
  submitted = false;
  error = false;

  private hash = '';
  private drawing = false;
  private lastX = 0;
  private lastY = 0;
  private ctx: CanvasRenderingContext2D | null = null;

  constructor(
    private route: ActivatedRoute,
    private signatureService: SignatureService
  ) {}

  ngOnInit(): void {
    this.hash = this.route.snapshot.paramMap.get('hash') ?? '';
    this.signatureService.getInfo(this.hash).subscribe({
      next: info => {
        this.info = info;
        this.loading = false;
        setTimeout(() => this.initCanvas(), 0);
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  private initCanvas(): void {
    if (!this.canvasRef) return;
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d');
    if (!this.ctx) return;
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    canvas.addEventListener('mousedown', e => this.startDraw(e.offsetX, e.offsetY));
    canvas.addEventListener('mousemove', e => { if (this.drawing) this.draw(e.offsetX, e.offsetY); });
    canvas.addEventListener('mouseup', () => this.stopDraw());
    canvas.addEventListener('mouseleave', () => this.stopDraw());

    canvas.addEventListener('touchstart', e => {
      e.preventDefault();
      const t = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      this.startDraw(t.clientX - rect.left, t.clientY - rect.top);
    }, { passive: false });
    canvas.addEventListener('touchmove', e => {
      e.preventDefault();
      if (!this.drawing) return;
      const t = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      this.draw(t.clientX - rect.left, t.clientY - rect.top);
    }, { passive: false });
    canvas.addEventListener('touchend', () => this.stopDraw());
  }

  private startDraw(x: number, y: number): void {
    this.drawing = true;
    this.lastX = x;
    this.lastY = y;
  }

  private draw(x: number, y: number): void {
    if (!this.ctx) return;
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.lastX = x;
    this.lastY = y;
  }

  private stopDraw(): void {
    this.drawing = false;
  }

  clearCanvas(): void {
    if (!this.ctx || !this.canvasRef) return;
    const canvas = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  isCanvasEmpty(): boolean {
    if (!this.ctx || !this.canvasRef) return true;
    const canvas = this.canvasRef.nativeElement;
    const data = this.ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    return !data.some(v => v !== 0);
  }

  submit(): void {
    if (this.isCanvasEmpty() || !this.canvasRef) return;
    const data = this.canvasRef.nativeElement.toDataURL('image/png');
    this.submitting = true;
    this.signatureService.saveSignature(this.hash, data).subscribe({
      next: () => {
        this.submitted = true;
        this.submitting = false;
      },
      error: () => {
        this.submitting = false;
      }
    });
  }
}
